import BasicTable from 'components/Form/Table';
import ModalCuradoriaFinanciamentos, {
  ActionButton,
} from 'components/Modal/ModaCuradoriaFinanciamentos';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Read } from 'services/financing-proposals';
import { Icon, StatusContent, Title } from 'ui/styled';
import { exposeStrapiError, marcadorDiferente, selectColors } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingDeskUnderAnalysisCuradoria() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression } = useContext(CoreContext);
  // Estados
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const opcao = 'curadoria';

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    const result = await Read(
      '?filters[status]=negado&filters[curadoria_analise][$ne]=recusado&populate=*'
    );
    setLoading(false);
    if (result && !exposeStrapiError(result)) {
      const sorted = result?.results?.sort((a, b) => b.id - a.id);
      setRegisters(sorted);
    }
  }, []);
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Verifica se foi analisado pela curadoria humana ou pelo bot
          const isAnalisadoCuradoria = row?.curadoria_analise === 'Analisado';
          const isAnalisadoBot = row?.curadoria_analise_bot === 'Analisado';
          const id = row?.id;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'Tempo',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador || user?.distribuidores?.length >= 1
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      {
        title: 'Status',
        ref: 'status',
        renderCell: ({ row }) => (
          <StatusContent color={selectColors(row?.status)}>
            {row?.status}
          </StatusContent>
        ),
      },
      {
        title: 'Ação',
        ref: 'action',
        renderCell: ({ row }) =>
          row?.status?.toLowerCase() === 'negado' ? (
            <ActionButton
              color="blue"
              onClick={() => setSelectedRow(row)}
              nospace
              small
              mbutton
            >
              Manter Negado
            </ActionButton>
          ) : null,
      },
      {
        title: '',
        ref: '',
        renderCell: ({ row }) => (
          <Icon
            icon="arrow-right"
            pointer
            onClick={() => navigate(`financing-table/${row?.id}/${opcao}`)}
          />
        ),
      },
    ];
    return baseColumns.filter(Boolean);
  }, [user, navigate]);
  // Linhas da tabela
  const rows = useMemo(() => {
    return registers.map((item) => ({
      id: item?.id,
      date: item?.data_em_analise,
      formatedDate: formatadores.TIMESTAMP_TO_BR(item?.data_em_analise),
      cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
      distributor: normalizeName(item?.distribuidor?.trade_name),
      integrator: normalizeName(item?.integrador?.nome_fantasia),
      customer: normalizeName(item?.informacoes_cliente?.nome_completo),
      CPF_CNPJ:
        item?.informacoes_cliente?.cpf ?? item?.informacoes_cliente?.cnpj,
      value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
      status: normalizeName(item?.status),
      curadoria_analise: item?.curadoria_analise,
      curadoria_analise_bot: item?.curadoria_analise_bot,
      cnpj_integrador: item?.integrador?.cnpj,
      cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
      cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
      cpf_cnpj_clean: item?.cliente?.cpf
        ? item?.cliente?.cpf.replace(/\D/g, '')
        : item?.cliente?.cnpj.replace(/\D/g, ''),
    }));
  }, [registers]);

  useEffect(() => init(), [tick, init]);

  return (
    <ContainerAuthenticated>
      <Title>Curadoria</Title>
      <TableWrapper title="Financiamentos" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
      <ModalCuradoriaFinanciamentos
        isOpen={!!selectedRow}
        row={selectedRow}
        reload={init}
        onClose={() => setSelectedRow(null)}
      />
    </ContainerAuthenticated>
  );
}
